/** Options de sexe pour les tableaux (aligné sur `player.sexe` / accessibleBoards). */
export const BOARD_SEXE_OPTIONS: { value: string; label: string }[] = [
  { value: "M", label: "Homme" },
  { value: "F", label: "Femme" },
];

/**
 * Codes catégorie d’âge licence FFTT (`player.categorie` / `tableau.cat`).
 * Valeurs observées dans les données joueurs (export 237485.json).
 * Ordre : jeunes → senior → vétérans.
 */
export const BOARD_CATEGORY_OPTIONS: readonly string[] = [
  "P",
  "B1",
  "B2",
  "C1",
  "C2",
  "J1",
  "J2",
  "J3",
  "J4",
  "M1",
  "M2",
  "S",
  "V40",
  "V45",
  "V50",
  "V55",
  "V60",
  "V65",
  "V70",
  "V75",
  "V80",
];

/** Libellés d’aide pour l’admin (les valeurs stockées restent les codes courts). */
export const BOARD_CATEGORY_LABELS: Partial<Record<string, string>> = {
  P: "Poussin",
  B1: "Benjamin — 1re année",
  B2: "Benjamin — 2e année",
  C1: "Cadet — 1re année",
  C2: "Cadet — 2e année",
  J1: "Junior — série 1",
  J2: "Junior — série 2",
  J3: "Junior — série 3",
  J4: "Junior — série 4",
  M1: "Minime — 1re année",
  M2: "Minime — 2e année",
  S: "Senior",
  V40: "Vétérans — 40 ans et +",
  V45: "Vétérans — 45 ans et +",
  V50: "Vétérans — 50 ans et +",
  V55: "Vétérans — 55 ans et +",
  V60: "Vétérans — 60 ans et +",
  V65: "Vétérans — 65 ans et +",
  V70: "Vétérans — 70 ans et +",
  V75: "Vétérans — 75 ans et +",
  V80: "Vétérans — 80 ans et +",
};

export function formatBoardCategoryMenuLabel(code: string): string {
  const hint = BOARD_CATEGORY_LABELS[code];
  return hint ? `${code} — ${hint}` : code;
}

/** Copie modifiable de la liste complète (sélection « toutes les catégories »). */
export function getAllBoardCategoryCodes(): string[] {
  return [...BOARD_CATEGORY_OPTIONS];
}

/** Indique si la sélection couvre exactement toutes les catégories connues. */
export function isFullBoardCategorySelection(cat: string[]): boolean {
  if (cat.length !== BOARD_CATEGORY_OPTIONS.length) return false;
  const set = new Set(cat);
  return BOARD_CATEGORY_OPTIONS.every((c) => set.has(c));
}

/** Regroupement pour l’admin : libellé au pluriel, codes enregistrés tels quels. */
export type BoardCategoryGroup = {
  id: string;
  /** Libellé affiché (famille, au pluriel si pertinent). */
  labelPlural: string;
  codes: readonly string[];
};

/**
 * Familles de catégories (FFT). Chaque code de `BOARD_CATEGORY_OPTIONS` apparaît
 * une seule fois dans un groupe.
 */
export const BOARD_CATEGORY_GROUPS: readonly BoardCategoryGroup[] = [
  { id: "p", labelPlural: "Poussins", codes: ["P"] },
  { id: "b", labelPlural: "Benjamins", codes: ["B1", "B2"] },
  { id: "c", labelPlural: "Cadets", codes: ["C1", "C2"] },
  { id: "j", labelPlural: "Juniors", codes: ["J1", "J2", "J3", "J4"] },
  { id: "m", labelPlural: "Minimes", codes: ["M1", "M2"] },
  { id: "s", labelPlural: "Seniors", codes: ["S"] },
  {
    id: "v",
    labelPlural: "Vétérans",
    codes: ["V40", "V45", "V50", "V55", "V60", "V65", "V70", "V75", "V80"],
  },
] as const;

const CATEGORY_ORDER_INDEX: Map<string, number> = new Map(
  BOARD_CATEGORY_OPTIONS.map((c, i) => [c, i])
);

/** Trie les codes dans l’ordre officiel (jeunes → vétérans). */
export function sortCategoryCodes(cat: string[]): string[] {
  return [...cat].sort(
    (a, b) =>
      (CATEGORY_ORDER_INDEX.get(a) ?? 999) - (CATEGORY_ORDER_INDEX.get(b) ?? 999)
  );
}

/** Ajoute ou retire tous les codes d’une famille. */
export function applyCategoryGroupToggle(
  cat: string[],
  codes: readonly string[],
  checked: boolean
): string[] {
  const set = new Set(cat);
  if (checked) {
    codes.forEach((c) => set.add(c));
  } else {
    codes.forEach((c) => set.delete(c));
  }
  return sortCategoryCodes(Array.from(set));
}

export function getCategoryGroupCheckboxState(
  cat: string[],
  codes: readonly string[]
): { checked: boolean; indeterminate: boolean } {
  const n = codes.filter((c) => cat.includes(c)).length;
  return {
    checked: n === codes.length && codes.length > 0,
    indeterminate: n > 0 && n < codes.length,
  };
}

export function isKnownBoardCategoryCode(code: string): boolean {
  return (BOARD_CATEGORY_OPTIONS as readonly string[]).includes(code);
}

/**
 * Valeurs encore présentes sur un tableau mais absentes de la liste officielle
 * (anciennes saisies libres à migrer).
 */
export function legacyBoardCategoryCodes(cat: string[]): string[] {
  return Array.from(
    new Set(cat.filter((c) => !isKnownBoardCategoryCode(c)))
  ).sort();
}

/**
 * Valeur stockée dans `handi` lorsque le tableau est réservé handisport
 * (chaîne truthy — cf. Cloud Function accessibleBoards).
 */
export const HANDISPORT_TABLE_FLAG = "handi";
